import { useRef, useState } from "react"
import * as Sentry from "@sentry/browser"
import { useAuth } from "../services/useAuth"

export const localStoragePrefix = "op_"

export const getLocalStorageItem = (key) => window.localStorage.getItem(`${localStoragePrefix}_${key}`)

const debounceMs = 500

// TODO(ando): I don't think there are any async operations in here
// we should come back to clean remove asyn and change usage
export const useLocalStorage = (key, initialValue, debounce = true) => {
  const writeTimeout = useRef(null)
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key for user
      const item = getLocalStorageItem(key)
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      Sentry.captureException(error)
      return initialValue
    }
  })
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    return new Promise((resolve, reject) => {
      try {
        setStoredValue((prevStoredValue) => {
          const valueToStore = value instanceof Function ? value(prevStoredValue) : value

          if (writeTimeout.current) clearTimeout(writeTimeout.current)
          writeTimeout.current = setTimeout(
            () => {
              try {
                // Save to local storage
                window.localStorage.setItem(`${localStoragePrefix}_${key}`, JSON.stringify(valueToStore))
                resolve()
              } catch (error) {
                Sentry.captureException(error)
                reject(error)
              }
            },
            debounce ? debounceMs : 0
          )
          return valueToStore
        })
      } catch (error) {
        // A more advanced implementation would handle the error case
        Sentry.captureException(error)
        reject(error)
      }
    })
  }

  const removeValue = () => {
    return new Promise((resolve, reject) => {
      try {
        // NOTE(ando): we don't want a debounced write to write after we've deleted 🙅
        if (writeTimeout.current) clearTimeout(writeTimeout.current)

        window.localStorage.removeItem(`${localStoragePrefix}_${key}`)
        setStoredValue(null)
        resolve()
      } catch (error) {
        Sentry.captureException(error)
        reject(error)
      }
    })
  }

  const checkAvailable = () => {
    return new Promise((resolve) => {
      const test = "op__test"
      try {
        window.localStorage.setItem(test, test)
        window.localStorage.removeItem(test)
        resolve(true)
      } catch (error) {
        console.log("[useLocalStorage]", "Local storage is unavailable")
        resolve(false)
      }
    })
  }

  return [storedValue, setValue, removeValue, { checkAvailable }]
}

export const useUserLocalStorage = (key, initialValue, debounce = true) => {
  const {
    principal: { userID }
  } = useAuth()
  const writeTimeout = useRef(null)

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key for user
      const item = window.localStorage.getItem(`${localStoragePrefix}${userID}_${key}`)
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      Sentry.captureException(error)
      return initialValue
    }
  })
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      setStoredValue((prevStoredValue) => {
        const valueToStore = value instanceof Function ? value(prevStoredValue) : value

        if (writeTimeout.current) clearTimeout(writeTimeout.current)
        writeTimeout.current = setTimeout(
          () => {
            // Save to local storage
            window.localStorage.setItem(`${localStoragePrefix}${userID}_${key}`, JSON.stringify(valueToStore))
          },
          debounce ? debounceMs : 0
        )

        return valueToStore
      })
    } catch (error) {
      // A more advanced implementation would handle the error case
      Sentry.captureException(error)
    }
  }

  const removeValue = () => {
    try {
      // NOTE(ando): we don't want a debounced write to write after we've deleted 🙅
      if (writeTimeout.current) clearTimeout(writeTimeout.current)

      window.localStorage.removeItem(`${localStoragePrefix}${userID}_${key}`)
      setStoredValue(null)
    } catch (error) {
      console.log("useUserLocalStorage", error)
      Sentry.captureException(error)
    }
  }

  return [storedValue, setValue, removeValue]
}
